import { useEffect, useMemo, useState } from "react";
import {
  CalendarClock,
  ChevronRight,
  Clock3,
  ShieldCheck,
  Users,
} from "lucide-react";
import { api, ApiError, query } from "../api";
import type { LeaveType, RequestStatus, Shift, Team, User } from "../types";
import {
  dateLabel,
  LEAVE_LABELS,
  localDateTime,
  SHIFT_LABELS,
  skillLabel,
  STATUS_LABELS,
  roleLabel,
  todayIso,
} from "../utils";
import {
  Alert,
  Card,
  Empty,
  Loading,
  PageHeader,
  SelectField,
  ShiftBadge,
} from "../components/ui";

interface OperationChief {
  id: string | number;
  name: string;
  title?: string | null;
}

interface OperationLeaveDetail {
  type?: LeaveType;
  duration?: "FULL" | "HALF" | "TWO_HOURS";
  startDate?: string;
  endDate?: string;
  description?: string | null;
  status?: RequestStatus;
  approverName?: string | null;
  approverRole?: string | null;
  approvedAt?: string | null;
  actedAsDelegate?: boolean;
}

interface OperationPerson {
  id: string | number;
  loginCode?: string;
  displayName: string;
  title?: string | null;
  team?: Team;
  schedulePattern?: string | null;
  skills?: string[];
  reason?: string;
  shift?: Shift;
  targetShift?: Shift;
  leave?: OperationLeaveDetail | null;
  request?: OperationLeaveDetail | null;
  approval?: {
    name?: string | null;
    role?: string | null;
    at?: string | null;
    delegated?: boolean;
  } | null;
  mission?: {
    id: string;
    location: string;
    taskName?: string | null;
    aircraftType?: string;
    engineType?: string;
  } | null;
}

interface SpecialOffPeriod {
  schedulePattern: string;
  label?: string;
  startDate?: string;
  endDate?: string;
  people?: OperationPerson[];
}

interface AttendanceSummary {
  available: number | null;
  total: number;
  rate: number | null;
  label: string | null;
  projectedAvailable: number | null;
  projectedRate: number | null;
  projectedLabel: string | null;
  supportCount: number;
  counts: {
    specialOff: number;
    fullAbsence: number;
    partialLeave: number;
    shiftChanges: number;
    pending: number;
    onDuty: number;
  };
  specialOffPeriods?: SpecialOffPeriod[];
}

type OperationGroups = Record<
  | "expectedWorkers"
  | "specialOff"
  | "approvedFullAbsences"
  | "partialLeaves"
  | "approvedShiftChanges"
  | "pendingRequests"
  | "onDuty",
  OperationPerson[]
>;

interface OperationTeam {
  team: Team;
  shift: Shift;
  chief: OperationChief | null;
  attendance: AttendanceSummary;
  groups: OperationGroups;
  units?: Array<{
    unit: "COMPOSITE" | "STRUCTURAL" | "PAINT";
    chief: OperationChief | null;
    attendance: AttendanceSummary;
    groups: OperationGroups;
  }>;
  records?: {
    leaves?: OperationPerson[];
    shiftChanges?: OperationPerson[];
  };
}

interface OperationSupervisor {
  id: string | number;
  name: string;
  managementShift: "R1" | "R2" | "DAY_08_17";
  shift: Exclude<Shift, "GECE" | "OFF"> | null;
  hours: string;
  matchedTeam?: Team | null;
}

interface OperationsData {
  date: string;
  teams: OperationTeam[];
  supervisors: OperationSupervisor[];
  handoffs?: Array<{
    fromName?: string | null;
    toName?: string | null;
    nightTeam?: Team;
    morningTeam?: Team;
    fromShift?: Shift;
    toShift?: Shift;
    summary?: string;
  }>;
}

const teams: Team[] = ["A", "B", "C", "D"];
const unitLabels = { COMPOSITE: "Kompozit", STRUCTURAL: "Yapısal", PAINT: "Boya" } as const;
const groupLabels: Array<[keyof OperationGroups, string]> = [
  ["expectedWorkers", "Beklenen çalışanlar"],
  ["specialOff", "Özel alt ekip OFF"],
  ["approvedFullAbsences", "Onaylı tam gün izin / yokluk"],
  ["partialLeaves", "Kısmi izin"],
  ["approvedShiftChanges", "Onaylı vardiya değişikliği"],
  ["pendingRequests", "Bekleyen talepler"],
  ["onDuty", "Görevde"],
];

function percent(value: number | null): string {
  if (value === null) return "—";
  return `%${new Intl.NumberFormat("tr-TR", { maximumFractionDigits: 1 }).format(value)}`;
}

function attendanceTone(rate: number | null): string {
  if (rate === null) return "off";
  if (rate >= 70) return "good";
  if (rate >= 40) return "low";
  return "critical";
}

function personDetail(person: OperationPerson): OperationLeaveDetail | null {
  return person.leave ?? person.request ?? null;
}

function PersonLine({ person }: { person: OperationPerson }) {
  const detail = personDetail(person);
  const approverName = detail?.approverName ?? person.approval?.name;
  const approverRole = detail?.approverRole ?? person.approval?.role;
  const approvedAt = detail?.approvedAt ?? person.approval?.at;
  return (
    <article className="operation-person">
      <div className="operation-person__main">
        <strong>{person.displayName}</strong>
        <span>
          {person.loginCode ? `${person.loginCode} · ` : ""}
          {person.title ?? "Ünvan belirtilmemiş"}
        </span>
        <small>
          {[person.team, person.schedulePattern, person.reason]
            .filter(Boolean)
            .join(" · ")}
        </small>
      </div>
      {person.skills?.length ? (
        <div className="operation-person__skills">
          {person.skills.map((item) => <b key={item}>{skillLabel(item)}</b>)}
        </div>
      ) : null}
      {detail ? (
        <div className="operation-person__leave">
          <span>
            {detail.type ? LEAVE_LABELS[detail.type] : "Kayıt"}
            {detail.duration === "HALF" ? " · Yarım gün" : null}
            {detail.duration === "TWO_HOURS" ? " · 2 saat" : null}
          </span>
          {detail.status ? <b>{STATUS_LABELS[detail.status]}</b> : null}
          {detail.startDate ? (
            <small>
              {dateLabel(detail.startDate, "short")}
              {detail.endDate && detail.endDate !== detail.startDate
                ? ` – ${dateLabel(detail.endDate, "short")}`
                : ""}
            </small>
          ) : null}
          {detail.description ? <p>{detail.description}</p> : null}
          {approverName ? (
            <small>
              {approverName}
              {approverRole ? ` · ${roleLabel(approverRole)}` : ""}
              {detail.actedAsDelegate || person.approval?.delegated
                ? " · Vekâleten"
                : ""}
              {approvedAt ? ` · ${localDateTime(approvedAt)}` : ""}
            </small>
          ) : null}
        </div>
      ) : null}
      {person.mission ? (
        <div className="operation-person__leave operation-person__mission">
          <span>Görevde: {person.mission.location}</span>
          {person.mission.taskName ? <b>{person.mission.taskName}</b> : null}
          <small>{[person.mission.aircraftType, person.mission.engineType].filter(Boolean).join(" · ")}</small>
        </div>
      ) : null}
    </article>
  );
}

function AttendanceLine({
  label,
  available,
  total,
  rate,
  state,
}: {
  label: string;
  available: number | null;
  total: number;
  rate: number | null;
  state: string | null;
}) {
  const tone = attendanceTone(rate);
  return (
    <div className={`attendance-line attendance-line--${tone}`}>
      <span>{label}</span>
      <strong>
        {available === null ? "Ana ekip bugün OFF" : `${available}/${total} — ${percent(rate)} — ${state ?? "—"}`}
      </strong>
      {rate !== null ? (
        <span className="attendance-line__bar" aria-hidden="true">
          <i style={{ width: `${Math.min(100, Math.max(0, rate))}%` }} />
        </span>
      ) : null}
    </div>
  );
}

function TeamOperationCard({
  item,
  filters,
}: {
  item: OperationTeam;
  filters: {
    pattern: string;
    status: string;
    leaveType: string;
    approver: string;
  };
}) {
  const filterPeople = (people: OperationPerson[]) => people.filter((person) => {
    const detail = personDetail(person);
    if (filters.pattern && person.schedulePattern !== filters.pattern) return false;
    if (filters.status && detail?.status !== filters.status) return false;
    if (filters.leaveType && detail?.type !== filters.leaveType) return false;
    const approver = detail?.approverName ?? person.approval?.name ?? "";
    if (filters.approver && approver !== filters.approver) return false;
    return true;
  });
  const attendance = item.attendance;
  const inactiveRecords = filterPeople([
    ...(item.records?.leaves ?? []),
    ...(item.records?.shiftChanges ?? []),
  ]).filter((person) => {
    const recordStatus = personDetail(person)?.status;
    return recordStatus === "REJECTED" || recordStatus === "CANCELLED" || recordStatus === "SUPERSEDED";
  });
  return (
    <Card className="operation-team-card">
      <header className="operation-team-card__header">
        <span className={`team-dot team-dot--${item.team.toLowerCase()}`}>{item.team}</span>
        <div>
          <h2>{item.team} Ekibi</h2>
          <span>Başteknisyen: {item.chief?.name ?? "Atanmamış"}</span>
        </div>
        <ShiftBadge shift={item.shift} />
      </header>

      <div className="attendance-summary">
        {item.units?.map((unit) => (
          <div key={unit.unit} className="operation-unit-summary">
            <small>{unitLabels[unit.unit]}{unit.chief ? ` · Başteknisyen: ${unit.chief.name}` : ""}</small>
            <AttendanceLine
              label={`${unitLabels[unit.unit]} planlanan devam`}
              available={unit.attendance.available}
              total={unit.attendance.total}
              rate={unit.attendance.rate}
              state={unit.attendance.label}
            />
          </div>
        ))}
        <AttendanceLine
          label="Toplam kesin planlanan devam"
          available={attendance.available}
          total={attendance.total}
          rate={attendance.rate}
          state={attendance.label}
        />
        {attendance.available !== null ? (
          <AttendanceLine
            label="Bekleyen talepler onaylanırsa"
            available={attendance.projectedAvailable}
            total={attendance.total}
            rate={attendance.projectedRate}
            state={attendance.projectedLabel}
          />
        ) : null}
        {attendance.supportCount > 0 ? (
          <small>+{attendance.supportCount} destek personeli bu vardiyaya geliyor.</small>
        ) : null}
      </div>

      <div className="operation-counts" aria-label="Operasyon özeti">
        <span><b>{attendance.counts.specialOff}</b> özel OFF</span>
        <span><b>{attendance.counts.fullAbsence}</b> tam gün yokluk</span>
        <span><b>{attendance.counts.partialLeave}</b> kısmi izin</span>
        <span><b>{attendance.counts.shiftChanges}</b> vardiya değişikliği</span>
        <span><b>{attendance.counts.pending}</b> bekleyen</span>
        <span><b>{attendance.counts.onDuty}</b> görevde</span>
      </div>

      {attendance.specialOffPeriods?.some((period) => !filters.pattern || period.schedulePattern === filters.pattern) ? (
        <div className="special-off-list">
          {attendance.specialOffPeriods
            .filter((period) => !filters.pattern || period.schedulePattern === filters.pattern)
            .map((period) => (
            <details key={`${period.schedulePattern}-${period.startDate ?? "today"}`}>
              <summary>
                <span>{period.label ?? `${period.schedulePattern} özel OFF döneminde`}</span>
                <ChevronRight aria-hidden="true" />
              </summary>
              {period.startDate ? (
                <small>
                  {dateLabel(period.startDate, "short")}
                  {period.endDate && period.endDate !== period.startDate
                    ? ` – ${dateLabel(period.endDate, "short")}`
                    : ""}
                </small>
              ) : null}
              <div className="operation-person-list">
                {period.people?.map((person) => <PersonLine key={person.id} person={person} />)}
              </div>
            </details>
          ))}
        </div>
      ) : null}

      <div className="operation-groups">
        {groupLabels.map(([key, label]) => {
          const people = filterPeople(item.groups?.[key] ?? []);
          if (!people.length) return null;
          return (
            <details key={key} open={key === "approvedFullAbsences" && people.length < 4}>
              <summary>
                <span>{label}</span>
                <b>{people.length}</b>
                <ChevronRight aria-hidden="true" />
              </summary>
              <div className="operation-person-list">
                {people.map((person) => <PersonLine key={`${key}-${person.id}`} person={person} />)}
              </div>
            </details>
          );
        })}
        {inactiveRecords.length ? (
          <details>
            <summary>
              <span>Reddedilen / iptal edilen kayıtlar</span>
              <b>{inactiveRecords.length}</b>
              <ChevronRight aria-hidden="true" />
            </summary>
            <div className="operation-person-list">
              {inactiveRecords.map((person, index) => (
                <PersonLine key={`inactive-${person.id}-${index}`} person={person} />
              ))}
            </div>
          </details>
        ) : null}
      </div>
    </Card>
  );
}

export function OperationsScreen({ currentUser }: { currentUser: User }) {
  const [date, setDate] = useState(todayIso());
  const [team, setTeam] = useState<Team | "">("");
  const [pattern, setPattern] = useState("");
  const [shift, setShift] = useState<Shift | "">("");
  const [status, setStatus] = useState("");
  const [leaveType, setLeaveType] = useState("");
  const [approver, setApprover] = useState("");
  const [data, setData] = useState<OperationsData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    let active = true;
    setLoading(true);
    setError("");
    api<OperationsData>(`/api/operations${query({ date, team })}`)
      .then((result) => { if (active) setData(result); })
      .catch((caught) => {
        if (active) setError(caught instanceof ApiError ? caught.message : "Operasyon bilgileri yüklenemedi.");
      })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [date, team]);

  const approvers = useMemo(() => {
    const result = new Set<string>();
    for (const item of data?.teams ?? []) {
      const collections = [
        ...Object.values(item.groups ?? {}),
        ...(item.records?.leaves ? [item.records.leaves] : []),
        ...(item.records?.shiftChanges ? [item.records.shiftChanges] : []),
      ];
      for (const group of collections) {
        for (const person of group as OperationPerson[]) {
          const name = personDetail(person)?.approverName ?? person.approval?.name;
          if (name) result.add(name);
        }
      }
    }
    return [...result].sort((a, b) => a.localeCompare(b, "tr"));
  }, [data]);
  const visibleTeams = (data?.teams ?? []).filter((item) => !shift || item.shift === shift);
  const filters = { pattern, status, leaveType, approver };

  return (
    <div className="page operations-page">
      <PageHeader
        eyebrow="SALT OKUNUR OPERASYON"
        title="Planlanan Devamlılık"
        description={`${dateLabel(date)} için vardiya, yokluk ve beklenen kadro görünümü. Gerçek turnike verisi değildir.`}
      />
      <Alert>
        Bu ekran operasyonel planlamayı gösterir. {currentUser.role === "admin" ? "Yönetici" : "Şef ve müdür"} rolünde kayıt değiştirilemez.
      </Alert>
      <Card className="operation-filters">
        <label className="field">
          <span className="field__label">Tarih</span>
          <input className="input" type="date" value={date} onChange={(event) => setDate(event.target.value)} />
        </label>
        <SelectField label="Ana ekip" value={team} onChange={(event) => { setTeam(event.target.value as Team | ""); setPattern(""); }}>
          <option value="">Tüm ekipler</option>
          {teams.map((item) => <option key={item} value={item}>{item} Ekibi</option>)}
        </SelectField>
        <SelectField label="Alt ekip" value={pattern} onChange={(event) => setPattern(event.target.value)}>
          <option value="">Tüm alt ekipler</option>
          {(team ? [team] : teams).flatMap((mainTeam) => [1, 2, 3, 4].map((number) => `${mainTeam}${number}`)).map((item) => <option key={item}>{item}</option>)}
        </SelectField>
        <SelectField label="Vardiya" value={shift} onChange={(event) => setShift(event.target.value as Shift | "")}>
          <option value="">Tüm vardiyalar</option>
          {Object.entries(SHIFT_LABELS).map(([value, label]) => <option value={value} key={value}>{label}</option>)}
        </SelectField>
        <SelectField label="Kayıt durumu" value={status} onChange={(event) => setStatus(event.target.value)}>
          <option value="">Tüm durumlar</option>
          {Object.entries(STATUS_LABELS).map(([value, label]) => <option value={value} key={value}>{label}</option>)}
        </SelectField>
        <SelectField label="İzin / yokluk türü" value={leaveType} onChange={(event) => setLeaveType(event.target.value)}>
          <option value="">Tüm türler</option>
          {Object.entries(LEAVE_LABELS).map(([value, label]) => <option value={value} key={value}>{label}</option>)}
        </SelectField>
        <SelectField label="Onaylayan" value={approver} onChange={(event) => setApprover(event.target.value)}>
          <option value="">Tüm başteknisyenler</option>
          {approvers.map((item) => <option key={item}>{item}</option>)}
        </SelectField>
      </Card>

      {error ? <Alert tone="danger">{error}</Alert> : null}
      {loading ? <Loading label="Planlanan devamlılık hesaplanıyor" /> : null}
      {!loading && data ? (
        <>
          {data.supervisors?.length ? (
            <section className="operation-supervisors">
              <div className="section-heading"><div><span className="eyebrow">YÖNETİM VARDİYASI</span><h2>Görevli şefler</h2></div></div>
              <div className="operation-supervisor-grid">
                {data.supervisors.map((supervisor) => (
                  <Card className="operation-supervisor" key={supervisor.id}>
                    <ShieldCheck aria-hidden="true" />
                    <div><strong>{supervisor.name}</strong><span>{supervisor.managementShift} · {supervisor.shift ? SHIFT_LABELS[supervisor.shift] : "Sabit yönetim"}</span><small>{supervisor.hours}{supervisor.matchedTeam ? ` · ${supervisor.matchedTeam} Ekibi` : ""}</small></div>
                  </Card>
                ))}
              </div>
            </section>
          ) : null}

          {visibleTeams.length ? (
            <div className="operation-team-grid">
              {visibleTeams.map((item) => <TeamOperationCard key={item.team} item={item} filters={filters} />)}
            </div>
          ) : (
            <Empty icon={<Users />} title="Ekip bulunamadı" text="Seçilen filtrelerle eşleşen operasyon kaydı yok." />
          )}

          {data.handoffs?.length ? (
            <section className="operation-handoffs">
              <div className="section-heading"><div><span className="eyebrow">EKİPLER ARASI</span><h2>Tahmini İş Devri</h2></div></div>
              <Card className="operation-handoff-list">
                {data.handoffs.map((item, index) => (
                  <div key={`${item.fromShift}-${item.toShift}-${index}`}>
                    <CalendarClock aria-hidden="true" />
                    <span>{item.summary ?? `${item.nightTeam ?? "—"} ${item.fromShift ? SHIFT_LABELS[item.fromShift] : ""} — ${item.fromName ?? "Başka personel"}`}</span>
                    <ChevronRight aria-hidden="true" />
                    <span>{item.morningTeam ?? "—"} {item.toShift ? SHIFT_LABELS[item.toShift] : ""} — {item.toName ?? "Başka personel"}</span>
                  </div>
                ))}
              </Card>
            </section>
          ) : null}
        </>
      ) : null}
      <p className="operation-footnote"><Clock3 size={15} /> Tarih ve saatler Europe/Istanbul zaman dilimindedir.</p>
    </div>
  );
}
